import {
  useAddCurrentUserFavoriteMutation,
  useDeleteCurrentUserFavoriteMutation,
} from 'gcs-common/clients/api/entities/current-user/current-user-api';
import { useCreateTaskMutation } from 'gcs-common/clients/api/entities/tasks/tasks-api';
import IconComponent from 'gcs-common/components/Icon/Icon';
import FORWARD_TARGET from 'gcs-common/constants/ForwardTarget';
import { ICON, ICON_COLOR, ICON_SIZE, ICON_TRANSFORM } from 'gcs-common/constants/IconConstants';
import { MESSAGE_TYPE } from 'gcs-common/constants/MessageTypes';
import { TOOL_TABS } from 'gcs-common/constants/toolUIFlags';
import { useSidebar } from 'gcs-common/design-system/ui/sidebar';
import { getSelectedChannelId } from 'gcs-common/slices/channels/channelsSelectors';
import { getIsAgent } from 'gcs-common/slices/currentUser/currentUserSelectors';
import {
  addSelectedMessageIds,
  channelInputReplyMessageStaged,
} from 'gcs-common/slices/messageInput/messageInputSlice';
import {
  getMessageAttributesText,
  getMessageBody,
  getMessageCanDelete,
  getMessageCanDownload,
  getMessageCanForward,
  getMessageCanReply,
  getMessageHasMedia,
  getMessageHasSyncPending,
  getMessageIsFavorite,
  getMessageIsForwarded,
  getMessageMediaFilename,
  getMessageMediaIsImage,
  getMessageType,
  getReplyMessageHasMedia,
  getVideoDownloadReady,
} from 'gcs-common/slices/messages/messageSelector';
import { getMediaDownloadIsLoading } from 'gcs-common/slices/messages/messagesSelectors';
import deleteMessage from 'gcs-common/slices/messages/messagesThunks/deleteMessage';
import downloadMedia from 'gcs-common/slices/messages/messagesThunks/downloadMedia';
import { getETSEnabledForChannel } from 'gcs-common/slices/uiState/uiStateSelectors';
import { setToolbarTab } from 'gcs-common/slices/uiState/uiStateSlice';
import { type MouseEvent, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import useWindowDimensions from '../../../customHooks/useWindowDimensions';
import Button from '../../Button/Button';
import PopupMenu from '../../PopupMenu/PopupMenu';
import { Spinner } from '../../Spinner/Spinner';
import styles from './styles.module.scss';

const MessageOptionsMenu = ({
  messageId,
  parent,
  showIcon,
}: {
  messageId: string;
  parent: React.RefObject<Element>;
  showIcon: boolean;
}) => {
  const selectedChannelId = useSelector(getSelectedChannelId) as string;
  const isFavorite = useSelector(getMessageIsFavorite(messageId)) as boolean;
  const canForward = useSelector(getMessageCanForward(selectedChannelId, messageId)) as boolean;
  const canDelete = useSelector(getMessageCanDelete(selectedChannelId, messageId)) as boolean;
  const canReply = useSelector(getMessageCanReply(selectedChannelId, messageId)) as boolean;
  const canDownload = useSelector(getMessageCanDownload(selectedChannelId, messageId)) as boolean;
  const mediaDownloadIsLoading = useSelector(getMediaDownloadIsLoading) as boolean;
  const isAgent = useSelector(getIsAgent);
  const isVideoDownloadReady = useSelector(getVideoDownloadReady(messageId)) as boolean;
  const messageType = useSelector(getMessageType(messageId)) as string;
  const isPending = useSelector(getMessageHasSyncPending(messageId)) as boolean;
  const replyHasMedia = useSelector(getReplyMessageHasMedia(messageId)) as boolean;
  const isForwarded = useSelector(getMessageIsForwarded(messageId)) as boolean;
  const etsEnabledForChannel = useSelector(getETSEnabledForChannel(selectedChannelId)) as boolean;
  const messageBody = useSelector(getMessageBody(messageId)) as string;
  const messageAttributesText = useSelector(getMessageAttributesText(messageId)) as string;
  const hasMedia = useSelector(getMessageHasMedia(messageId)) as boolean;
  const isImage = useSelector(getMessageMediaIsImage(messageId)) as boolean;
  const fileName = useSelector(getMessageMediaFilename(messageId)) as string;
  const createTaskMutation = useCreateTaskMutation();
  const addFavoriteMutation = useAddCurrentUserFavoriteMutation();
  const deleteFavoriteMutation = useDeleteCurrentUserFavoriteMutation();
  const dispatch = useDispatch();
  const sidebar = useSidebar();

  const handleTodoAdd = () => {
    let description = messageBody;
    if (!description) {
      description = messageAttributesText;
    }

    sidebar.setOpen(true);
    dispatch(setToolbarTab({ tab: TOOL_TABS.TODOS }));

    if (hasMedia) {
      if (!description) {
        if (fileName) {
          description = fileName;
        } else {
          description = isImage ? 'Bild' : 'Datei';
        }
      }
      if (!isImage) {
        description = `📎 ${description}`;
      }
    } else if (!description) {
      description = 'Nachricht';
    }

    createTaskMutation.mutate({
      body: {
        id: uuidv4(),
        description,
        messageId,
        channelId: selectedChannelId,
      },
    });
  };

  const handleFavoriteAdd = () => {
    addFavoriteMutation.mutate({
      body: {
        messageId: messageId,
        channelId: selectedChannelId,
      },
    });
  };

  const handleFavoriteRemove = () => {
    deleteFavoriteMutation.mutate({ messageId });
  };

  const handleDeleteMessage = useCallback(async () => {
    dispatch(deleteMessage({ messageId }));
  }, [dispatch, messageId]);

  const handleForwardToChat = useCallback(() => {
    dispatch(
      addSelectedMessageIds({
        selectedMessageId: messageId,
        channelId: selectedChannelId,
        target: FORWARD_TARGET.CHAT,
      }),
    );
  }, [dispatch, messageId, selectedChannelId]);

  const handleReplyToMessage = useCallback(() => {
    dispatch(channelInputReplyMessageStaged({ channelId: selectedChannelId, messageId }));
  }, [dispatch, messageId, selectedChannelId]);

  const handleForwardToEmail = useCallback(async () => {
    dispatch(
      addSelectedMessageIds({
        selectedMessageId: messageId,
        channelId: selectedChannelId,
        target: FORWARD_TARGET.EMAIL,
      }),
    );
  }, [dispatch, messageId, selectedChannelId]);

  const enableEtsTicketCreation = useCallback(async () => {
    dispatch(
      addSelectedMessageIds({
        selectedMessageId: messageId,
        channelId: selectedChannelId,
        target: FORWARD_TARGET.TICKET,
      }),
    );
    sidebar.setOpen(true);
    dispatch(setToolbarTab({ tab: TOOL_TABS.TICKETS }));
  }, [dispatch, messageId, selectedChannelId, sidebar]);

  const { height: viewportHeight } = useWindowDimensions();

  const [showMenu, setShowMenu] = useState(false);
  const [isRight, setIsRight] = useState(false);
  const [isTop, setIsTop] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const isVideo = messageType === MESSAGE_TYPE.VIDEO;
  const isMedia = messageType === MESSAGE_TYPE.MEDIA || messageType === MESSAGE_TYPE.VIDEO;

  const handleToggleMenu = () => {
    if (!showMenu && buttonRef && buttonRef.current && parent?.current) {
      setIsRight(parent.current.getBoundingClientRect().right - buttonRef.current.getBoundingClientRect().right < 80);
      setIsTop(viewportHeight - buttonRef.current.getBoundingClientRect().top < viewportHeight / 2);
    }
    setShowMenu(!showMenu);
  };

  const onDownloadMedia = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // @ts-expect-error -> remove soon
    await dispatch(downloadMedia({ messageId }));
    handleToggleMenu();
  };

  return (
    <div
      ref={buttonRef}
      tabIndex={0}
      role="button"
      className={isMedia && !isForwarded ? styles.msgMediaOptionsButton : styles.msgOptionsButton}
      onKeyUp={() => handleToggleMenu()}
      onClick={() => handleToggleMenu()}
      title="Nachrichtenmenü"
    >
      {showMenu && (
        <PopupMenu
          onHide={setShowMenu}
          isVisible={showMenu}
          menuClass={isRight ? styles.menuRight : styles.menuLeft}
          upward={isTop}
        >
          {canReply && (
            <Button onClick={handleReplyToMessage} className={styles.textAlign}>
              <span className={styles.optionsIconWrapper}>
                <IconComponent Icon={ICON.FORWARD} transform={ICON_TRANSFORM.FLIP_HORIZONTAL} alt="forward icon" />
              </span>
              Antworten
            </Button>
          )}
          {!replyHasMedia && canDownload && (
            <Button onClick={onDownloadMedia} className={styles.textAlign} disabled={isVideo && !isVideoDownloadReady}>
              {mediaDownloadIsLoading || (isVideo && !isVideoDownloadReady) ? (
                <Spinner />
              ) : (
                <span className={styles.optionsIconWrapper}>
                  <IconComponent Icon={ICON.DOWNLOAD} alt="Download icon" />
                </span>
              )}
              Herunterladen
            </Button>
          )}
          <Button onClick={handleTodoAdd} className={styles.textAlign} disabled={isPending}>
            <span className={styles.optionsIconWrapper}>
              <IconComponent Icon={ICON.CHECKLIST} alt="Todo icon" />
            </span>
            Als Todo markieren
          </Button>
          {isFavorite ? (
            <Button onClick={handleFavoriteRemove} className={styles.textAlign}>
              <span className={styles.optionsIconWrapper}>
                <IconComponent Icon={ICON.STAR_FILLED} alt="Favoriten icon" />
              </span>
              Von Favoriten entfernen
            </Button>
          ) : (
            <Button onClick={handleFavoriteAdd} className={styles.textAlign} disabled={isPending}>
              <span className={styles.optionsIconWrapper}>
                <IconComponent Icon={ICON.STAR} alt="Favoriten icon" />
              </span>
              Zu Favoriten hinzufügen
            </Button>
          )}
          {canForward && (
            <Button onClick={handleForwardToChat} className={styles.textAlign}>
              <span className={styles.optionsIconWrapper}>
                <IconComponent Icon={ICON.FORWARD} alt="forward icon" />
              </span>
              Weiterleiten
            </Button>
          )}
          {canDelete && (
            <Button onClick={handleDeleteMessage} className={styles.textAlign} disabled={isPending}>
              <span className={styles.optionsIconWrapper}>
                <IconComponent Icon={ICON.DELETE} alt="Delete icon" />
              </span>
              Nachricht löschen
            </Button>
          )}
          {isAgent && (
            <Button onClick={handleForwardToEmail} className={styles.textAlign} disabled={isPending}>
              <span className={styles.optionsIconWrapper}>
                <IconComponent Icon={ICON.EMAIL} alt="Email icon" />
              </span>
              Als Email verschicken
            </Button>
          )}
          {etsEnabledForChannel && (
            <Button
              onClick={enableEtsTicketCreation}
              className={styles.textAlign}
              disabled={isPending || (isVideo && !isVideoDownloadReady)}
            >
              <span className={styles.optionsIconWrapper}>
                <IconComponent Icon={ICON.ETS} alt="ticket icon" size={ICON_SIZE.SMALL} />
              </span>
              ETS-Ticket erstellen
            </Button>
          )}
        </PopupMenu>
      )}
      {showIcon && (
        <IconComponent Icon={ICON.ARROW} transform={ICON_TRANSFORM.ROTATE_90} color={ICON_COLOR.DARKEST_GREY} />
      )}
    </div>
  );
};

export default MessageOptionsMenu;
